import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { ReviewRow, ReviewsApi } from 'entities/Review'
import { tableLimitStore } from 'entities/TableLimit'
import { type IReviewsListParams } from 'entities/Review/api/types'

export class ReviewsListStore {
  tableStore
  scrollToTopTrigger = ''
  private _search = ''
  private _initialLoading = true
  private _itemsMap = new Map<number, ReviewRow>()
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null

  constructor() {
    this.tableStore = new TableStore<ReviewRow>({
      key: 'reviews-list',
      page: 1,
      limit: 10,
      sortOrder: 'asc',
      getLimit: () => tableLimitStore.getLimit('reviews-table'),
      setLimit: (value) => tableLimitStore.setLimit('reviews-table', value),
      useColumnsCache: true,
    })
    makeAutoObservable(this)
  }

  init = () => {
    void this.loadData()
    this._reactionParamsWithoutPage()
    this._reactionParamsGetItems()
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this.tableStore.setLoading(true)
      })

      try {
        const { data } = await ReviewsApi.getReviews(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })

        runInAction(() => {
          this._itemsMap.clear()
          data.data.forEach((item) => {
            if (item.id) {
              this._itemsMap.set(item.id, new ReviewRow(item))
            }
          })

          this.tableStore.setTotal(data.meta.total)
          this.scrollToTopTrigger = nanoid()
        })
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 408) {
          logger.warn(
            'TABLE: reviews-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const newLimit = tableLimitStore.reduceLimit('reviews-table', this.tableStore.limit)
          this.tableStore.setLimit(newLimit)
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this._initialLoading = false
        this.tableStore.setLoading(false)
      })
    },
  })

  onSuccessDelete = (id: number) => {
    this.tableStore.setLoading(true)
    this._itemsMap.delete(id)
    if (this._itemsMap.size === 0 && this.tableStore.page > 1) {
      this.tableStore.setPage(this.tableStore.page - 1)
    } else {
      void this.loadData()
    }
  }

  private _reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        runInAction(() => {
          this.tableStore.setLoading(true)
          this.tableStore.setPage(1)
        })
      }
    )
  }

  private _reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  setSearch = (value: string) => {
    this._search = value
  }

  resetPaginationPage = () => {
    this.tableStore.setPage(1)
    this.tableStore.setLimit(this.cachedLimit)
  }

  dispose = () => {
    this.resetPaginationPage()
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
  }

  get search() {
    return this._search
  }

  get isInitialLoading() {
    return this._initialLoading
  }

  get cachedLimit() {
    return tableLimitStore.getLimit('reviews-table') ?? 10
  }

  get isEmpty() {
    return (
      !this._initialLoading &&
      !this.tableStore.loading &&
      !this._search &&
      this._itemsMap.size === 0
    )
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get paramsWithoutPage(): Omit<IReviewsListParams, 'page'> {
    return {
      per_page: this.tableStore.limit,
      search: this._search,
      sortBy: this.tableStore.sortBy || undefined,
      order: this.tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IReviewsListParams {
    return {
      page: this.tableStore.page,
      ...this.paramsWithoutPage,
    }
  }
}
